import {Connection, Edge} from 'reactflow';

import {assert} from 'Asserts';
import {
  ProcessExecutionType,
  ProcessFlowchartNodeDetailsType,
  ProcessSingletonTaskSchemaName,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ProcessFlowchartForValidation,
  ProcessFlowchartValidationError,
  ValidationContext,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {
  ProcessTaskDefinitionVersionGraphQL,
  ProcessFlowchartNodeType,
  SidepanelTab,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  FlowchartNode,
  TaskDefinitionVersionSpecWithTaskSchema,
  TaskSchemaNodeSectionData,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {SidepanelSize} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';

/**
 * Whether the node sidepanel can be opened via a single click or a double click.
 * With double click, the node will have an edit/view icon in the node toolbar.
 */
export enum FlowchartNodeEditMode {
  SINGLE_CLICK = 'single_click',
  DOUBLE_CLICK = 'double_click',
}

/**
 * Context data needed for creating new nodes
 */
export interface NodeCreationContext {
  // TODO: Determine if this is sufficient for both workflows + procedures
  // Workflows will use this to extract some information for new schemas (ex: org)
  // Procedures will need the unit op singleton, but this will be a procedures singleton task schema...
  // Expecting this context to evolve in future PRs
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  singletonTaskSchemaForExecutionType: ProcessTaskSchemaWithoutFlowchartConfigGraphQL | null;
  existingNodeNames?: Array<string>;
}

export const DEFAULT_MAX_EDGES = 1000;

/**
 * Shared contract for connection and deletion rules that apply to nodes in the flowchart.
 *
 * Used by both {@link ProcessFlowchartNodeAdapter} (e.g. Router, End nodes) and
 * {@link ProcessFlowchartTaskSchemaNodeAdapter} (task-schema nodes). The editor consults these
 * methods when: validating edge connections, enabling/disabling delete, and determining valid
 * drop targets. Implement all four methods.
 */
abstract class ProcessFlowchartNodeAdapterBlockingRules {
  /**
   * Whether the user can delete this node type from the canvas.
   * If false, the delete action and keyboard shortcut are disabled for this node type.
   */
  abstract isDeletable(): boolean;

  /**
   * Maximum number of edges that may target a single node of this type.
   * The editor blocks new connections once this count is reached. Use {@link DEFAULT_MAX_EDGES}
   * for default value. For output nodes, `isFlowchartOutput` is true and some types
   * allow only one incoming edge.
   */
  abstract maxIncomingEdges(isFlowchartOutput?: boolean): number;

  /**
   * Maximum number of edges that may originate from a single node of this type.
   * The editor blocks new connections once this count is reached. Use {@link DEFAULT_MAX_EDGES}
   * for default value.
   */
  abstract maxOutgoingEdges(isFlowchartOutput?: boolean): number;

  /**
   * Node types that must not be directly connected to this node type (in either direction).
   * Used to block invalid topologies (e.g. no edge from End to Task). Return an array of
   * {@link ProcessFlowchartNodeType} values; the editor will reject any connection that
   * would create adjacency with a listed type.
   */
  abstract blockedAdjacentNodeTypes(): Array<ProcessFlowchartNodeType>;
}

/**
 * Base adapter for non–task-schema node types in the flowchart (e.g. Router, End/Stop).
 *
 * Use this when adding a new **node type** that is not tied to a task execution type—such as
 * control-flow nodes. One adapter instance per node type; the adapter is identified by
 * {@link nodeType}. For nodes that represent task schemas (and execution types), extend
 * {@link ProcessFlowchartTaskSchemaNodeAdapter} instead.
 */
export abstract class ProcessFlowchartNodeAdapter extends ProcessFlowchartNodeAdapterBlockingRules {
  /**
   * The flowchart node type this adapter handles (e.g. ROUTER_NODE, END_NODE).
   * The editor uses this to look up the adapter when rendering or validating a node.
   */
  abstract readonly nodeType: ProcessFlowchartNodeType;

  /**
   * Run flowchart-level validation specific to this node type.
   * Invoked during validation passes. Return errors for invalid configurations involving this
   * node type; return an empty array if no errors. Optional per-node validation can be
   * implemented in task-schema adapters via {@link ProcessFlowchartTaskSchemaNodeAdapter.validateFlowchartForNodeType}.
   */
  abstract validateFlowchartForNodeType(
    flowchart: ProcessFlowchartForValidation,
    validationContext: ValidationContext
  ): Array<ProcessFlowchartValidationError>;

  /**
   * Optional: validate a single connection (source → target) for this node type.
   * Called after default rules (max edges, blocked adjacent types). Return true to allow,
   * false to reject. If not implemented, the connection is allowed once default rules pass.
   */
  abstract isConnectionValid?(connection: Connection, existingEdges: Array<Edge>): boolean;
}

/**
 * Convenience base for simple non–task-schema node types (e.g. Router, End/Stop).
 *
 * Provides default implementations for all {@link ProcessFlowchartNodeAdapterBlockingRules}:
 * deletable, unlimited incoming/outgoing edges, no blocked adjacent types, and connection
 * allowed. Override only what you need (e.g. {@link blockedAdjacentNodeTypes} or
 * {@link isConnectionValid}). Also provides a no-op {@link nodeDetailsSidepanelContent};
 * override to show custom sidepanel content when the node is selected.
 */
export abstract class DefaultProcessFlowchartNodeAdapter extends ProcessFlowchartNodeAdapter {
  /** Override to prevent deletion of specific node types. */
  isDeletable(): boolean {
    return true;
  }

  /** Override to change the maximum number of incoming edges for this node type. */
  maxIncomingEdges(isFlowchartOutput?: boolean): number {
    return DEFAULT_MAX_EDGES;
  }

  /** Override to change the maximum number of outgoing edges for this node type. */
  maxOutgoingEdges(isFlowchartOutput?: boolean): number {
    return DEFAULT_MAX_EDGES;
  }

  /** Override to prevent connections between this node type and specific adjacent node types. */
  blockedAdjacentNodeTypes(): Array<ProcessFlowchartNodeType> {
    return [];
  }

  /** Override to add custom connection validation logic. */
  isConnectionValid?(connection: Connection, existingEdges: Array<Edge>): boolean {
    return true;
  }

  /** Override to display content in the node details sidepanel when this node is selected. */
  nodeDetailsSidepanelContent(_selectedNode: FlowchartNode): JSX.Element | null {
    return null;
  }
}

export interface TaskSchemaDisplayOptions {
  rightSidepanelSize?: SidepanelSize;
  rightSidepanelNameOverride?: (nodeName: string, selectedFieldName: string | null) => string;
}

/**
 * Base type for adapters that extend the processes editor with a new task execution type.
 *
 * When extending the processes system with a new execution type, application teams provide a subtype
 * of this class to tell the editor how to render and work with the new type. Each adapter
 * is specific to one task execution type, as defined by the `executionType` associated with the node.
 *
 * Node adapters are provided to the editor via plugins. See ProcessesPluginBase for more
 * information.
 *
 * See this confluence page for more information on plugins: https://benchling.atlassian.net/wiki/spaces/Workflows/pages/1285619729/Adding+A+Processes+Plugin
 */
export abstract class ProcessFlowchartTaskSchemaNodeAdapter<
  TFrontendApplicationData,
  TNetworkApplicationData,
> extends ProcessFlowchartNodeAdapterBlockingRules {
  nodeType: ProcessFlowchartNodeType = ProcessFlowchartNodeType.TASK_SCHEMA_NODE;

  /**
   * The task execution type this adapter handles (e.g. PROCEDURE, PROCEDURE_METHOD, DIRECT).
   * The editor uses this to resolve the adapter for a node from its task schema's execution type.
   */
  abstract readonly executionType: ProcessExecutionType;

  /**
   * Human-readable name for this node type (e.g. "Procedure", "Direct"). Shown in validation
   * messages and other UI that refers to the node type.
   */
  abstract readonly typeDisplayName: string;

  /**
   * How the node details sidepanel is opened: single click (default) or double click.
   * With DOUBLE_CLICK, the node shows an edit/view icon in the toolbar instead of opening
   * on first click.
   */
  abstract readonly editMode: FlowchartNodeEditMode;

  /**
   * Whether the node name can be edited via double-click directly on the canvas.
   * When true, double-clicking the node name will allow inline editing.
   * When false (default), the node name can only be edited via the sidepanel.
   *
   * When overriding to true, the adapter must implement `onUpdateNodeName`
   * to handle updating the underlying model data with the new name.
   */
  readonly isNodeNameEditableInline: boolean = false;

  /**
   * Updates the task definition version spec with a new node name.
   * This is called when the user edits the node name via double-click on the canvas.
   * Only called when `isNodeNameEditableInline` is true.
   *
   * @param currentSpec - The current task definition version spec to update.
   * @param newName - The new name for the node.
   * @returns The updated task definition version spec with the new name set in the application data,
   *          or null if this adapter does not support name editing.
   */
  onUpdateNodeName(
    currentSpec: TaskDefinitionVersionSpecWithTaskSchema<TFrontendApplicationData>,
    newName: string
  ): TaskDefinitionVersionSpecWithTaskSchema<TFrontendApplicationData> | null {
    assert(
      this.isNodeNameEditableInline,
      'Node name double click editing is not enabled for procedure method nodes'
    );
    return null;
  }

  /**
   * Convert a task definition version from the GQL type to the frontend application data type.
   * The frontend application data type should be the desired appliciation format for interacting with
   * the node's sidepanel content, etc.
   *
   * @param taskDefinitionVersionGQL - The task definition version GQL to convert.
   * @returns The converted task definition version with frontend application data.
   */
  abstract convertTaskDefinitionVersionGQLToApplicationData(
    taskDefinitionVersionGQL: ProcessTaskDefinitionVersionGraphQL
  ): TFrontendApplicationData;

  /**
   * Convert a task definition version from the frontend application data type to the network application data type.
   * The network application data type should be the desired format for the backend network spec.
   *
   * @param taskDefinitionVersionSpec - The task definition version spec to convert.
   * @returns The converted task definition version with network application data.
   */
  abstract convertTaskDefinitionVersionSpecToNetworkApplicationData(
    taskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema<TFrontendApplicationData>,
    flowchartConfigId: string
  ): TNetworkApplicationData;

  /**
   * Provides a subtitle to use when rendering the node on the flowchart canvas.
   *
   * @param taskDefinitionVersion - The task definition version for the node being rendered.
   * @param isFlowchartOutput - If this adapter is for a node associated with the flowchart execution type,
   * this flag signals if the start or output node is being rendered.
   * @returns The subtitle, or null if no subtitle should be displayed.
   */
  abstract getNodeSubtitle(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<TFrontendApplicationData>,
    isFlowchartOutput?: boolean
  ): string | null;

  /**
   * Provides an icon to use when rendering the node on the flowchart canvas.
   *
   * @param taskDefinitionVersion - The task definition version for the node being rendered.
   * @param isFlowchartOutput - If this adapter is for a node associated with the flowchart execution type,
   * this flag signals if the start or output node is being rendered.
   * @returns The icon name, or null if no icon should be displayed.
   */
  abstract getNodeIcon(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<TFrontendApplicationData>,
    isFlowchartOutput?: boolean
  ): BenchlingIconName | null;

  /**
   * Creates the sections of input and output parameters to use when rendering the node on the
   * flowchart canvas.
   *
   * Each section provides a custom grouping of the node's input and output parameters to use when
   * displaying the node. Application teams can use this to group parameters into logical sections
   * based on their functionality.
   *
   * @param taskDefinitionVersion - The task definition version for the node being rendered.
   * @param nodeDetailsType - The type of node being rendered.
   * @returns The sections to display on the node on the canvas.
   */
  abstract createSections(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<TFrontendApplicationData>,
    nodeDetailsType: ProcessFlowchartNodeDetailsType
  ): Array<TaskSchemaNodeSectionData>;

  abstract nodeDetailsSidepanelContent: Array<SidepanelTab>;

  /**
   * Optionally update a task definition version spec using its application data.
   *
   * Some execution types store their effective field definitions inside application data and do not
   * receive updated task definition version field definitions from the backend until persistence.
   * Adapters can override this to derive field definitions, and potentially other properties, from application data so that flowchart validation stays accurate.
   *
   * PTDVs use field definitions, but some domains (e.g., Procedures) use schema fields.
   * Adapters must convert by overriding this method.
   */
  updateTaskDefinitionVersionWithApplicationData(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<TFrontendApplicationData>
  ): TaskDefinitionVersionSpecWithTaskSchema<TFrontendApplicationData> {
    return taskDefinitionVersion;
  }

  /**
   * Runs the node-creation flow for this execution type (e.g. open a modal, collect inputs,
   * then return the new node spec). Called when the user adds a new task node of this type
   * to the flowchart. Return the new {@link TaskDefinitionVersionSpecWithTaskSchema} to insert,
   * or null if the user cancels. Use {@link NodeCreationContext} for root schema and singleton
   * schema (if any).
   *
   * @param context - Root task schema, optional singleton for this execution type, and existing node names.
   * @returns Promise resolving to the new node spec to add to the flowchart, or null if cancelled.
   */
  abstract onCreateNewNode(
    context: NodeCreationContext
  ): Promise<TaskDefinitionVersionSpecWithTaskSchema<TFrontendApplicationData> | null>;

  /**
   * Get the details of a new node to be created.
   *
   * @param context Context data needed for node creation
   * @returns The details of the new node to be created. Subtitle and icon are optional.
   */
  abstract getNewNodeDetails(context: NodeCreationContext): {
    nodeName: string;
    nodeSubtitle: string | null;
    nodeIcon: BenchlingIconName | null;
  };

  /**
   * Provides the name of the singleton task schema to use when creating a new node.
   * If null, will create a new task schema of the execution type for the given adapter.
   *
   * @returns The name of the singleton task schema to use when creating a new node.
   */
  abstract getSingletonTaskSchemaName(
    rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL
  ): ProcessSingletonTaskSchemaName | null;

  validateFlowchartForNodeType(
    flowchart: ProcessFlowchartForValidation,
    validationContext: ValidationContext
  ): Array<ProcessFlowchartValidationError> {
    return [];
  }

  /**
   * Provides a name to use when rendering the node on the flowchart canvas.
   * This is default implementation that can be overridden by adapters.
   *
   * Handles incrementing based on existing nodes with the same base name.
   * For example, if "Task Schema 1" exists, the next one will be "Task Schema 2".
   *
   * @param taskDefinitionVersion - The task definition version for the node.
   * @param isFlowchartOutput - If adapter is for a node associated with the flowchart execution type,
   * this flag signals if this node is the start or end node.
   * @param existingNodeNames - Array of existing node names on the canvas for incrementing logic
   * @returns The default name for the node.
   */
  abstract getNodeName(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<TFrontendApplicationData>,
    isFlowchartOutput?: boolean,
    existingNodeNames?: Array<string>
  ): string;

  /**
   * Get the field name for a given field ID by searching through the node's sections.
   * This includes dynamically added sections like "Map from Start".
   *
   * @param node - The flowchart node to search within.
   * @param fieldId - The client ID of the field to find.
   * @returns The field name, or null if not found.
   */
  getFieldNameFromFieldId(node: FlowchartNode, fieldId: string): string | null {
    // Default implementation - override in subclasses if needed
    return null;
  }

  getDisplayOptions(): TaskSchemaDisplayOptions {
    return {
      rightSidepanelSize: SidepanelSize.MEDIUM,
      rightSidepanelNameOverride: (nodeName: string, _selectedFieldName: string | null) =>
        `Node ${nodeName} Details`,
    };
  }

  /**
   * Optional: Add custom connection validation rules for this node type.
   * This is called AFTER default validation passes.
   *
   * @param connection - The connection being attempted
   * @param existingEdges - Array of existing edges to check for duplicates
   * @returns true if connection should be allowed, false to reject
   */
  isConnectionValid?(connection: Connection, existingEdges: Array<Edge>): boolean;

  isDeletable(): boolean {
    return true;
  }

  maxIncomingEdges(isFlowchartOutput?: boolean): number {
    return DEFAULT_MAX_EDGES;
  }

  maxOutgoingEdges(isFlowchartOutput?: boolean): number {
    return DEFAULT_MAX_EDGES;
  }

  blockedAdjacentNodeTypes(): Array<ProcessFlowchartNodeType> {
    return [ProcessFlowchartNodeType.STOP_NODE];
  }
}
